import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import type { DocSection } from '@/data/docs/types';

// Раздел считается текущим, когда его заголовок поднялся под шапку (64 px) с запасом.
const ACTIVE_LINE = 120;

function useActiveSection(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      let current = ids[0] ?? null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= ACTIVE_LINE) current = id;
      }
      // Короткий последний раздел может не дойти до верха экрана.
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (atBottom && ids.length > 0) current = ids[ids.length - 1];
      setActive(current);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ids]);

  return active;
}

export default function DocsToc({ sections, ids }: { sections: DocSection[]; ids: string[] }) {
  const active = useActiveSection(ids);
  return (
    <nav aria-label="На этой странице">
      <p className="text-[13px] font-medium text-ink">На этой странице</p>
      <ul className="mt-3 border-l border-ink/10">
        {sections.map((section) => (
          <li key={section.id}>
            <Link
              to={`#${section.id}`}
              aria-current={active === section.id ? 'location' : undefined}
              className={clsx(
                '-ml-px block border-l py-1.5 pl-4 text-[13.5px] leading-snug transition-colors duration-200',
                active === section.id
                  ? 'border-ink font-medium text-ink'
                  : 'border-transparent text-ink/65 hover:text-ink',
              )}
            >
              {section.title}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
